import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useMemo } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { formatReportNumber, pivotToneRule, ReportLink, ReportTone, toneBackground } from '../reportCells';
import { buildPivot, PivotSpec } from './pivotData';

type Props<Row> = {
    rows: Row[];
    spec: PivotSpec<Row>;
    /** The header of each row key column, outermost first. */
    rowHeaders: string[];
    columnLabel: (column: string) => string;
    /** The table's accessible name. */
    label: string;
    decimals?: number;
    /** A Total column at the right end of each row. */
    showRowTotals?: boolean;
    /** A totals row between the header and the rows, where the list tables put theirs. */
    showColumnTotals?: boolean;
    /** The tone of the figure the cells sum, as its column has in the table reports; a loss is red. */
    tone: ReportTone;
};

/**
 * Report rows as a pivot table: one row per row key, one column per column key, each cell the sum of its
 * rows. A cell takes the figure's tone in the step of its share of the largest cell, so the heavy months or
 * people stand out.
 */
export function PivotTable<Row>({
    rows,
    spec,
    rowHeaders,
    columnLabel,
    label,
    decimals = 2,
    showRowTotals = false,
    showColumnTotals = false,
    tone,
}: Props<Row>) {
    const pivot = useMemo(() => buildPivot(rows, spec), [rows, spec]);
    const rule = pivotToneRule(tone);
    const range = { max: pivot.maxCell, min: -pivot.maxCell };
    const format = (value: number | undefined) => (value == null ? '' : formatReportNumber(value, decimals));
    const totalLabel = t('reporting.chart.total');
    return (
        <Table aria-label={label} className="text-meta tabular-nums">
            <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                    {rowHeaders.map((header, index) => (
                        <TableHead
                            key={header || index}
                            scope="col"
                            className={cn(
                                'text-muted-foreground px-3 font-medium',
                                index === 0 && 'bg-muted sticky left-0'
                            )}
                        >
                            {header}
                        </TableHead>
                    ))}
                    {pivot.columns.map((column) => (
                        <TableHead
                            key={column}
                            scope="col"
                            className="text-muted-foreground px-3 text-right font-medium"
                        >
                            {columnLabel(column)}
                        </TableHead>
                    ))}
                    {showRowTotals && (
                        <TableHead scope="col" className="px-3 text-right font-semibold">
                            {totalLabel}
                        </TableHead>
                    )}
                </TableRow>
                {showColumnTotals && (
                    <TableRow className="bg-card hover:bg-card border-b-2 font-semibold">
                        <TableHead
                            scope="row"
                            colSpan={rowHeaders.length}
                            className="bg-card text-foreground sticky left-0 px-3 font-semibold"
                        >
                            {t('totals.totals')}
                        </TableHead>
                        {pivot.columns.map((column) => (
                            <TableCell key={column} className="px-3 text-right">
                                {format(pivot.columnTotals.get(column))}
                            </TableCell>
                        ))}
                        {showRowTotals && <TableCell className="px-3 text-right">{format(pivot.grandTotal)}</TableCell>}
                    </TableRow>
                )}
            </TableHeader>
            <TableBody>
                {pivot.rows.map((row) => (
                    <TableRow key={row.key} className="hover:bg-transparent">
                        {row.labels.map((rowLabel, index) =>
                            index === 0 ? (
                                <TableHead
                                    key={index}
                                    scope="row"
                                    className="bg-card sticky left-0 h-auto px-3 py-1.5 font-medium"
                                >
                                    <ReportLink to={row.href}>{rowLabel ?? '–'}</ReportLink>
                                </TableHead>
                            ) : (
                                <TableCell key={index} className="text-muted-foreground px-3 py-1.5">
                                    {rowLabel ?? '–'}
                                </TableCell>
                            )
                        )}
                        {pivot.columns.map((column) => {
                            const value = row.cells.get(column);
                            return (
                                <TableCell
                                    key={column}
                                    className="px-3 py-1.5 text-right"
                                    style={{ backgroundColor: toneBackground(value, rule, range) }}
                                >
                                    {format(value)}
                                </TableCell>
                            );
                        })}
                        {showRowTotals && (
                            <TableCell className="px-3 py-1.5 text-right font-semibold">{format(row.total)}</TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
